/**
 * cart-discount-progress.js
 *
 * Responsibility: Drive the tiered discount progress bar in the cart drawer.
 *
 * AGENTS.md compliance:
 * - No global variables. All state is local to the IIFE.
 * - All DOM access uses data-* attributes only.
 * - DOM queries are centralised in getElements().
 * - Business logic (calculateProgress, formatRupees) is pure — no DOM dependency.
 * - DOM logic (applyProgressToDOM) only reads inputs and applies outputs.
 * - Observers are scoped, debounced, and never trigger network requests.
 * - Network sync fires only on explicit user-initiated cart events with timestamp cache-busting.
 * - Every DOM access is guarded. Risky paths use try/catch.
 */

(function initCartDiscountProgress() {
  'use strict';

  // ─── Configuration ─────────────────────────────────────────────────────────

  const DISCOUNT_TIERS = [
    { thresholdPaise: 99900,  discountPct: 10, discountCode: 'WEEB10',  tierLabel: '10% off' },
    { thresholdPaise: 259900, discountPct: 15, discountCode: 'LVLUP15', tierLabel: '15% off' },
    { thresholdPaise: 399900, discountPct: 20, discountCode: 'MAX20',   tierLabel: '20% off' },
  ];

  const MAX_THRESHOLD_PAISE = DISCOUNT_TIERS[DISCOUNT_TIERS.length - 1].thresholdPaise;

  // CSS custom property names — JS passes data; CSS owns the layout
  const CSS_VAR_FILL_WIDTH    = '--discount-fill-width';
  const CSS_VAR_MASCOT_LEFT   = '--discount-mascot-left';

  // Data attribute name for milestone unlocked state
  const ATTR_MILESTONE_UNLOCKED = 'data-unlocked';

  // ─── Debounce ───────────────────────────────────────────────────────────────

  /**
   * @param {Function} fn
   * @param {number} waitMs
   * @returns {Function}
   */
  function debounce(fn, waitMs) {
    let timerId = null;
    return function debounced() {
      if (timerId !== null) clearTimeout(timerId);
      timerId = setTimeout(function () {
        timerId = null;
        fn();
      }, waitMs);
    };
  }

  // ─── Pure business logic ────────────────────────────────────────────────────

  /**
   * Calculates all progress state from a raw paise value.
   * Pure function — no DOM or side effects.
   *
   * @param {number} cartTotalPaise
   * @returns {{
   *   fillPct: number,
   *   mascotPct: number,
   *   unlockedCount: number,
   *   unlockedTiers: Array,
   *   nextTier: object|null,
   *   amountNeededPaise: number
   * }}
   */
  function calculateProgress(cartTotalPaise) {
    const safeTotal = Math.max(0, cartTotalPaise || 0);

    const unlockedTiers = DISCOUNT_TIERS.filter(function (tier) {
      return safeTotal >= tier.thresholdPaise;
    });

    let nextTier = null;
    for (let i = 0; i < DISCOUNT_TIERS.length; i++) {
      if (safeTotal < DISCOUNT_TIERS[i].thresholdPaise) {
        nextTier = DISCOUNT_TIERS[i];
        break;
      }
    }

    const amountNeededPaise = nextTier ? nextTier.thresholdPaise - safeTotal : 0;

    const rawFillPct = (Math.min(safeTotal, MAX_THRESHOLD_PAISE) / MAX_THRESHOLD_PAISE) * 100;

    // Cap fill so it never visually reaches an unearned milestone
    let fillPct = rawFillPct;
    if (nextTier) {
      const nextMilestonePct = (nextTier.thresholdPaise / MAX_THRESHOLD_PAISE) * 100;
      const cappedMaxPct = nextMilestonePct - 2;
      if (fillPct > cappedMaxPct) fillPct = cappedMaxPct;
    }

    // Mascot follows fill, clamped to safe visible range
    const mascotPct = Math.min(Math.max(fillPct, 4), 100);

    const isFreeGoodiesUnlocked = safeTotal >= 200000;

    return {
      fillPct: fillPct,
      mascotPct: mascotPct,
      unlockedCount: unlockedTiers.length,
      unlockedTiers: unlockedTiers,
      nextTier: nextTier,
      amountNeededPaise: amountNeededPaise,
      isFreeGoodiesUnlocked: isFreeGoodiesUnlocked,
    };
  }

  /**
   * Formats paise to a displayable INR string.
   * Pure function — no DOM.
   *
   * @param {number} paise
   * @returns {string}
   */
  function formatRupees(paise) {
    try {
      return '\u20b9' + Math.ceil(paise / 100).toLocaleString('en-IN');
    } catch (e) {
      return '\u20b9' + Math.ceil(paise / 100);
    }
  }

  // ─── DOM access (centralised) ───────────────────────────────────────────────

  /**
   * Returns all required element refs via data-* attributes only.
   * Returns null if the component root is absent.
   *
   * @returns {object|null}
   */
  function getElements() {
    try {
      const root = document.querySelector('[data-cart-discount-progress]');
      if (!root) return null;

      return {
        root: root,
        fill: root.querySelector('[data-discount-fill]'),
        mascot: root.querySelector('[data-discount-mascot]'),
        nextLabel: root.querySelector('[data-discount-next-label]'),
        banner: root.querySelector('[data-discount-banner]'),
        bannerText: root.querySelector('[data-discount-banner-text]'),
        freeShippingText: root.querySelector('[data-free-shipping-text]'),
        milestones: Array.from(root.querySelectorAll('[data-discount-milestone]')),
      };
    } catch (e) {
      return null;
    }
  }

  // ─── DOM updates ────────────────────────────────────────────────────────────

  /**
   * Applies a computed progress object to the DOM.
   * Toggles data attributes and state classes.
   *
   * @param {object} elements - Result of getElements()
   * @param {object} progress - Result of calculateProgress()
   */
  function applyProgressToDOM(elements, progress) {
    if (!elements || !progress) return;

    try {
      // Set custom property and direct style for instant rendering across all mobile browsers
      if (elements.fill) {
        elements.fill.style.setProperty(CSS_VAR_FILL_WIDTH, progress.fillPct.toFixed(2) + '%');
        elements.fill.style.width = progress.fillPct.toFixed(2) + '%';
      }

      // Pass mascot position
      if (elements.mascot) {
        elements.mascot.style.setProperty(CSS_VAR_MASCOT_LEFT, progress.mascotPct.toFixed(2) + '%');
        elements.mascot.style.left = progress.mascotPct.toFixed(2) + '%';
      }

      // Next milestone hint label
      if (elements.nextLabel) {
        elements.nextLabel.textContent = progress.nextTier
          ? formatRupees(progress.amountNeededPaise) + ' away from ' + progress.nextTier.tierLabel
          : 'Max discount unlocked!';
        elements.nextLabel.setAttribute('data-has-next-tier', progress.nextTier ? 'true' : 'false');
      }

      // Milestone dots — toggle data-unlocked attribute
      if (elements.milestones && elements.milestones.length > 0) {
        elements.milestones.forEach(function (milestone) {
          const tierIndex = parseInt(milestone.getAttribute('data-discount-milestone'), 10);
          const isUnlocked = progress.unlockedCount > tierIndex;
          milestone.setAttribute(ATTR_MILESTONE_UNLOCKED, isUnlocked ? 'true' : 'false');
        });
      }

      // Discount banner state
      if (elements.banner && elements.bannerText) {
        const isDiscountUnlocked = progress.unlockedTiers.length > 0;
        if (isDiscountUnlocked) {
          const bestTier = progress.unlockedTiers[progress.unlockedTiers.length - 1];
          elements.bannerText.textContent =
            bestTier.discountPct + '% discount unlocked \u2014 use code ' +
            bestTier.discountCode + ' at checkout';
          elements.banner.setAttribute('data-state', 'unlocked');
        } else if (progress.nextTier) {
          elements.bannerText.textContent =
            'Add ' + formatRupees(progress.amountNeededPaise) +
            ' more to unlock ' + progress.nextTier.discountPct + '% off';
          elements.banner.setAttribute('data-state', 'locked');
        }
        elements.banner.setAttribute('data-visible', 'true');
      }

      // Free shipping + goodies row
      if (elements.freeShippingText) {
        if (progress.isFreeGoodiesUnlocked) {
          elements.freeShippingText.innerHTML =
            "Yay! You've got <strong>free shipping</strong> + <strong>free goodies</strong>";
        } else {
          elements.freeShippingText.innerHTML =
            "Yay! You've got <strong>free shipping</strong>";
        }
      }
    } catch (e) {
      // Guard against non-fatal DOM access exceptions
    }
  }

  // ─── Cart total reader ──────────────────────────────────────────────────────

  /**
   * Reads the cart total in paise from DOM attributes or subtotal text.
   * @returns {number} paise
   */
  function readCartPaiseFromDOM() {
    try {
      const root = document.querySelector('[data-cart-discount-progress]');
      if (root) {
        const attrPaise = parseInt(root.getAttribute('data-cart-total-paise') || '0', 10);
        if (attrPaise > 0) return attrPaise;
      }

      // Fallback 1: estimated total element (after discount)
      const totalEl = document.querySelector('[data-testid="cart-total-value"], .cart-totals__total-value, [ref="cartTotal"], [data-cart-subtotal]');
      if (totalEl) {
        const rawAttr = totalEl.getAttribute('value');
        const rawText = (rawAttr || totalEl.textContent || '').replace(/,/g, '').replace(/[^\d.]/g, '').trim();
        if (rawText) {
          const parsedNum = parseFloat(rawText);
          if (!isNaN(parsedNum) && parsedNum > 0) {
            return Math.round(parsedNum * 100);
          }
        }
      }

      return 0;
    } catch (e) {
      return 0;
    }
  }

  // ─── Update Orchestration ───────────────────────────────────────────────────

  function updateFromDOM() {
    const elements = getElements();
    if (!elements) return;
    const paise = readCartPaiseFromDOM();
    applyProgressToDOM(elements, calculateProgress(paise));
  }

  /**
   * Fetches authoritative cart total from /cart.js with timestamp cache-buster.
   */
  function syncFromNetwork() {
    try {
      const cacheBustUrl = '/cart.js?t=' + Date.now();
      fetch(cacheBustUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
        .then(function (cartData) {
          const paise = typeof cartData.total_price === 'number'
            ? cartData.total_price
            : cartData.items_subtotal_price;

          if (typeof paise === 'number') {
            const elements = getElements();
            if (elements) {
              elements.root.setAttribute('data-cart-total-paise', String(paise));
              applyProgressToDOM(elements, calculateProgress(paise));
            }
          } else {
            updateFromDOM();
          }
        })
        .catch(function () {
          updateFromDOM();
        });
    } catch (e) {
      updateFromDOM();
    }
  }

  const debouncedUpdateFromDOM = debounce(updateFromDOM, 50);
  const debouncedSyncFromNetwork = debounce(syncFromNetwork, 150);

  // ─── Observers ──────────────────────────────────────────────────────────────

  function initObservers() {
    // Watch cart drawer container or document.body for DOM morphs and subtree replacements
    const drawerContainer = document.querySelector('theme-drawer#cart-drawer') || document.body;
    if (drawerContainer) {
      const drawerObserver = new MutationObserver(function (mutations) {
        let hasRelevantMutation = false;
        for (let i = 0; i < mutations.length; i++) {
          const m = mutations[i];
          if (m.type === 'childList' || m.attributeName === 'data-cart-total-paise' || m.attributeName === 'open') {
            hasRelevantMutation = true;
            break;
          }
        }
        if (hasRelevantMutation) {
          debouncedUpdateFromDOM();
        }
      });

      drawerObserver.observe(drawerContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-cart-total-paise', 'open'],
      });
    }
  }

  // ─── Event listeners ────────────────────────────────────────────────────────

  function handleCartEvent(event) {
    try {
      const cartData = event && event.detail && event.detail.cart ? event.detail.cart : null;
      if (cartData) {
        let paise = null;
        if (typeof cartData.total_price === 'number') {
          paise = cartData.total_price;
        } else if (typeof cartData.items_subtotal_price === 'number') {
          paise = cartData.items_subtotal_price;
        } else if (cartData.cost && cartData.cost.totalAmount && typeof cartData.cost.totalAmount.amount === 'number') {
          paise = Math.round(cartData.cost.totalAmount.amount * 100);
        } else if (cartData.cost && cartData.cost.subtotalAmount && typeof cartData.cost.subtotalAmount.amount === 'number') {
          paise = Math.round(cartData.cost.subtotalAmount.amount * 100);
        }

        if (paise !== null) {
          const elements = getElements();
          if (elements) {
            elements.root.setAttribute('data-cart-total-paise', String(paise));
            applyProgressToDOM(elements, calculateProgress(paise));
          }
        }
      }
      debouncedSyncFromNetwork();
    } catch (e) {
      debouncedSyncFromNetwork();
    }
  }

  function attachEventListeners() {
    // Standard Shopify and theme cart events
    document.addEventListener('shopify:cart:lines-update', handleCartEvent);
    document.addEventListener('shopify:cart:discount-update', handleCartEvent);
    document.addEventListener('shopify:cart:view', handleCartEvent);
    document.addEventListener('shopify:section:load', function () {
      updateFromDOM();
      debouncedSyncFromNetwork();
    });

    document.addEventListener('cart:updated', handleCartEvent);
    document.addEventListener('cart:refresh', handleCartEvent);
    document.addEventListener('cart:change', handleCartEvent);

    // Drawer open / visibility events
    document.addEventListener('theme-drawer:open', function () {
      updateFromDOM();
      debouncedSyncFromNetwork();
    });
    document.addEventListener('drawer:open', function () {
      updateFromDOM();
      debouncedSyncFromNetwork();
    });

    // Global click listener on quantity change buttons and cart remove buttons
    document.addEventListener('click', function (event) {
      const target = event.target;
      if (!target || !(target instanceof Element)) return;

      const isQtyButton = target.closest('button[name="plus"], button[name="minus"], .quantity__button, [data-quantity-button], [data-cart-remove]');
      if (isQtyButton) {
        debouncedSyncFromNetwork();
      }
    });
  }

  // ─── Boot ───────────────────────────────────────────────────────────────────

  function init() {
    updateFromDOM();
    syncFromNetwork();
    initObservers();
    attachEventListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
